import { useMemo } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import {
  type Company,
  type CompanyGetSuccess,
  type DeliveryDateReference,
  GetCustomerCompanyDetailsDocument,
  type Location,
  type LocationDeliveryPackagingPreferenceGetSuccess,
  type LocationGetSuccess,
  type Query,
} from "@/graphql/codegen/graphql"

interface UseGetCustomerCompanyDetailsReturn {
  company: Company | undefined
  shipToLocation: Location | undefined
  preferredDeliveryDateReference: DeliveryDateReference | undefined
  isFetching: boolean
  error: CombinedError | undefined
}

export const useGetCustomerCompanyDetails = (
  companyId: string,
  shipToLocationId: string
): UseGetCustomerCompanyDetailsReturn => {
  const [{ data, fetching, error }] = useQuery<
    Pick<Query, "company" | "location" | "locationDeliveryPackagingPreference">
  >({
    query: GetCustomerCompanyDetailsDocument,
    variables: { companyId, shipToLocationId },
    pause: !companyId || !shipToLocationId,
  })

  const company = useMemo(() => {
    const success = data?.company.get as CompanyGetSuccess | undefined
    return success?.company
  }, [data?.company.get])

  const shipToLocation = useMemo(() => {
    const success = data?.location.get as LocationGetSuccess | undefined
    return success?.location
  }, [data?.location.get])

  const preferredDeliveryDateReference = useMemo(() => {
    const success = data?.locationDeliveryPackagingPreference.get as
      | LocationDeliveryPackagingPreferenceGetSuccess
      | undefined
    return success?.preference?.preferredDeliveryDateReference ?? undefined
  }, [data?.locationDeliveryPackagingPreference.get])

  return { company, shipToLocation, preferredDeliveryDateReference, isFetching: fetching, error }
}
